// see docs: https://router.vuejs.org/
import Vue from 'vue';
import Router from 'vue-router';
import { ROUTER_NAME, ROUTER_PAGE_TITLE } from '../enums/router';
import { USER_ROLE } from '../enums/user';

// use router
Vue.use(Router);

const Login = () => import('@/views/Login');
const ProfileEdit = () => import('@/views/ProfileEdit');
const StockBrowser = () => import('@/views/StockBrowser');
const PageNotFound = () => import('@/views/PageNotFound');
const PagePermissionDenied = () => import('@/views/PagePermissionDenied');
const TheContainer = () => import('@/containers/TheContainer');
const UserList = () => import('@/views/UserList');
const UserCreate = () => import('@/views/UserCreate');
const UserEdit = () => import('@/views/UserEdit');
const OrderList = () => import('@/views/OrderList');
const OrderSingle = () => import('@/views/OrderSingle');
const StockBrowserBranchSingle = () => import('@/views/StockBrowserBranchSingle');
const StockBrowserSkuSingle = () => import('@/views/StockBrowserSkuSingle');
const ChannelList = () => import('@/views/ChannelList');
const ChannelCreate = () => import('@/views/ChannelCreate');
const ChannelSingle = () => import('@/views/ChannelSingle');
const AllocationOnlineStockList = () => import('@/views/AllocationOnlineStockList');
const AllocationOnlineStockCreate = () => import('@/views/AllocationOnlineStockCreate');
const AllocationOnlineStockSingle = () => import('@/views/AllocationOnlineStockSingle');
const AllocationChannelList = () => import('@/views/AllocationChannelList');
const AllocationChannelCreate = () => import('@/views/AllocationChannelCreate');
const AllocationChannelSingle = () => import('@/views/AllocationChannelSingle');
const AllocationOnlineGroup = () => import('@/views/AllocationOnlineGroup');
const Dashboard = () => import('@/views/Dashboard');
const Reports = () => import('@/views/Reports');
const Errors = () => import('@/views/Errors');
const AuditLogs = () => import('@/views/AuditLogs');
const AuditLogDetail = () => import('@/views/AuditLogDetail');

const router = new Router({
	mode: 'history',
	routes: [
		{
			path: '/',
			redirect: '/login',
		},
		{
			path: '/login',
			name: ROUTER_NAME.LOGIN,
			component: Login,
		},
		{
			path: '/',
			component: TheContainer,
			children: [
				// User Profile
				{
					path: '/profile',
					name: ROUTER_NAME.PROFILE_EDIT,
					component: ProfileEdit,
					meta: {
						pageTitle: ROUTER_PAGE_TITLE.EDIT_PROFILE,
						breadcrumbs: false,
						requireAuth: true,
					},
				},
				// Stock Browser
				{
					path: 'stock-browser',
					redirect: '404',
					component: {
						template: `<router-view :key="$route.fullPath" />`,
					},
					children: [
						{
							// Stock Browser
							path: '',
							name: ROUTER_NAME.STOCK_BROWSER,
							component: StockBrowser,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.STOCK_BROWSER,
								breadcrumbs: false,
								requireAuth: true,
							},
						},
						{
							// Stock Browser Branch
							path: 'branch/:id',
							name: ROUTER_NAME.STOCK_BROWSER_BRANCH_SINGLE,
							component: StockBrowserBranchSingle,
							meta: {
								pageTitle: null,
								breadcrumbs: [
									{ title: ROUTER_PAGE_TITLE.STOCK_BROWSER, route: { name: ROUTER_NAME.STOCK_BROWSER } },
								],
								requireAuth: true,
							},
						},
						{
							// Stock Browser Branch
							path: 'sku/:id',
							name: ROUTER_NAME.STOCK_BROWSER_SKU_SINGLE,
							component: StockBrowserSkuSingle,
							meta: {
								pageTitle: null,
								breadcrumbs: [
									{
										title: ROUTER_PAGE_TITLE.STOCK_BROWSER,
										route: {
											name: ROUTER_NAME.STOCK_BROWSER,
											query: {
												tab: 'sku',
											},
										},
									},
								],
								requireAuth: true,
							},
						},
					],
				},
				// Order List
				{
					path: 'orders',
					redirect: '404',
					component: {
						template: `<router-view :key="$route.fullPath" />`,
					},
					children: [
						{
							path: '',
							name: ROUTER_NAME.ORDER_LIST,
							component: OrderList,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.ORDER_LIST,
								breadcrumbs: false,
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN, USER_ROLE.USER],
							},
						},
						{
							path: ':id',
							name: ROUTER_NAME.ORDER_SINGLE,
							component: OrderSingle,
							meta: {
								pageTitle: null,
								breadcrumbs: [
									{ title: ROUTER_PAGE_TITLE.ORDER_LIST, route: { name: ROUTER_NAME.ORDER_LIST } },
								],
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN, USER_ROLE.USER],
							},
						},
					],
				},
				// User Management
				{
					path: 'users',
					redirect: '404',
					component: {
						template: `<router-view :key="$route.fullPath" />`,
					},
					children: [
						{
							// User List
							path: '',
							name: ROUTER_NAME.USER_LIST,
							component: UserList,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.USER_LIST,
								breadcrumbs: false,
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN, USER_ROLE.USER],
							},
						},
						{
							// User Create
							path: 'create',
							name: ROUTER_NAME.USER_CREATE,
							component: UserCreate,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.USER_CREATE,
								breadcrumbs: [
									{
										title: ROUTER_PAGE_TITLE.USER_LIST,
										route: {
											name: ROUTER_NAME.USER_LIST,
										},
									},
								],
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN],
							},
						},
						{
							// User Edit
							path: ':id',
							name: ROUTER_NAME.USER_EDIT,
							component: UserEdit,
							meta: {
								pageTitle: null,
								breadcrumbs: [
									{
										title: ROUTER_PAGE_TITLE.USER_LIST,
										route: {
											name: ROUTER_NAME.USER_LIST,
										},
									},
								],
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN, USER_ROLE.USER],
							},
						},
					],
				},
				// Online Group Management
				{
					path: 'online-group',
					redirect: '404',
					component: {
						template: `<router-view :key="$route.fullPath" />`,
					},
					children: [
						{
							path: '',
							name: ROUTER_NAME.ALOCATION_ONLINE_GROUP,
							component: AllocationOnlineGroup,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.ALOCATION_ONLINE_GROUP,
								breadcrumbs: false,
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN, USER_ROLE.USER],
							},
						},
					],
				},
				// Channel Management
				{
					path: 'channels',
					redirect: '404',
					component: {
						template: `<router-view :key="$route.fullPath" />`,
					},
					children: [
						{
							// Channel List
							path: '',
							name: ROUTER_NAME.CHANNEL_LIST,
							component: ChannelList,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.CHANNEL_LIST,
								breadcrumbs: false,
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN, USER_ROLE.USER],
							},
						},
						{
							// Channel Create
							path: 'create',
							name: ROUTER_NAME.CHANNEL_CREATE,
							component: ChannelCreate,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.CHANNEL_CREATE,
								breadcrumbs: [
									{
										title: ROUTER_PAGE_TITLE.CHANNEL_SINGLE,
										route: {
											name: ROUTER_NAME.CHANNEL_LIST,
										},
									},
								],
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN],
							},
						},
						{
							// Channel Single
							path: ':id',
							name: ROUTER_NAME.CHANNEL_SINGLE,
							component: ChannelSingle,
							meta: {
								pageTitle: null,
								breadcrumbs: [
									{
										title: ROUTER_PAGE_TITLE.CHANNEL_SINGLE,
										route: {
											name: ROUTER_NAME.CHANNEL_LIST,
										},
									},
								],
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN, USER_ROLE.USER],
							},
						},
					],
				},
				// Allocation
				{
					path: 'allocation-rule',
					redirect: '404',
					component: {
						template: `<router-view :key="$route.fullPath" />`,
					},
					children: [
						// Allocate to online stock
						{
							path: 'online-stock',
							name: ROUTER_NAME.ALOCATION_ONLINE_STOCK_LIST,
							component: AllocationOnlineStockList,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.ALOCATION_ONLINE_STOCK_LIST,
								breadcrumbs: false,
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN, USER_ROLE.USER],
							},
						},
						// Allocate to online stock, create
						{
							path: 'online-stock/create',
							name: ROUTER_NAME.ALOCATION_ONLINE_STOCK_CREATE,
							component: AllocationOnlineStockCreate,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.ALOCATION_ONLINE_STOCK_CREATE,
								breadcrumbs: [
									{
										title: ROUTER_PAGE_TITLE.ALOCATION_ONLINE_STOCK_LIST,
										route: {
											name: ROUTER_NAME.ALOCATION_ONLINE_STOCK_LIST,
										},
									},
								],
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN],
							},
						},
						// Allocate to online stock, single
						{
							path: 'online-stock/:id',
							name: ROUTER_NAME.ALOCATION_ONLINE_STOCK_SINGLE,
							component: AllocationOnlineStockSingle,
							meta: {
								pageTitle: null,
								breadcrumbs: [
									{
										title: ROUTER_PAGE_TITLE.ALOCATION_ONLINE_STOCK_SINGLE,
										route: {
											name: ROUTER_NAME.ALOCATION_ONLINE_STOCK_LIST,
										},
									},
								],
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN, USER_ROLE.USER],
							},
						},
						// Allocate to channels
						{
							path: 'channel',
							name: ROUTER_NAME.ALOCATION_CHANNEL_LIST,
							component: AllocationChannelList,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.ALOCATION_CHANNEL_LIST,
								breadcrumbs: false,
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN, USER_ROLE.USER],
							},
						},
						// Allocate to channels, Create
						{
							path: 'channel/create',
							name: ROUTER_NAME.ALOCATION_CHANNEL_CREATE,
							component: AllocationChannelCreate,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.ALOCATION_CHANNEL_CREATE,
								breadcrumbs: [
									{
										title: ROUTER_PAGE_TITLE.ALOCATION_CHANNEL_LIST,
										route: {
											name: ROUTER_NAME.ALOCATION_CHANNEL_LIST,
										},
									},
								],
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN],
							},
						},
						// Allocate to channels, Single
						{
							path: 'channel/:id',
							name: ROUTER_NAME.ALOCATION_CHANNEL_SINGLE,
							component: AllocationChannelSingle,
							meta: {
								pageTitle: null,
								breadcrumbs: [
									{
										title: ROUTER_PAGE_TITLE.ALOCATION_CHANNEL_SINGLE,
										route: {
											name: ROUTER_NAME.ALOCATION_CHANNEL_LIST,
										},
									},
								],
								requireAuth: true,
								requireRoles: [USER_ROLE.ADMIN, USER_ROLE.USER],
							},
						},
					],
				},
				// Dashboard
				{
					path: 'dashboard',
					name: ROUTER_NAME.DASHBOARD,
					component: Dashboard,
					meta: {
						pageTitle: ROUTER_PAGE_TITLE.DASHBOARD,
						breadcrumbs: false,
						requireAuth: true,
					},
				},
				// REPORTS
				{
					path: 'reports',
					component: {
						template: `<router-view :key="$route.fullPath" />`,
					},
					children: [
						{
							path: '',
							name: ROUTER_NAME.REPORTS,
							component: Reports,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.REPORTS,
								breadcrumbs: false,
								requireAuth: true,
							},
						},
					],
				},
				// Error
				{
					path: 'errors',
					redirect: '404',
					component: {
						template: `<router-view :key="$route.fullPath" />`,
					},
					children: [
						{
							// Stock Browser
							path: '',
							name: ROUTER_NAME.ERRORS,
							component: Errors,
							meta: {
								pageTitle: ROUTER_PAGE_TITLE.ERRORS,
								breadcrumbs: false,
								requireAuth: true,
							},
						},
					],
				},
				// Audit logs
				{
					path: '/audit-logs',
					redirect: '/audit-logs',
					name: ROUTER_NAME.AUDIT_LOGS,
					component: {
						template: `<router-view :key="$route.fullPath" />`,
					},
					children: [
						{
							path: '/',
							name: ROUTER_NAME.AUDIT_LOGS_LIST,
							component: AuditLogs,
							meta: {
								requireAuth: true,
								breadcrumbs: false,
								pageTitle: ROUTER_PAGE_TITLE.AUDIT_LOG_LIST,
							},
						},
						// Audit log Detail
						{
							path: ':id',
							name: ROUTER_NAME.AUDIT_LOG_DETAIL,
							component: AuditLogDetail,
							meta: {
								requireAuth: true,
								breadcrumbs: [
									{ title: 'Audit log', route: { name: ROUTER_NAME.AUDIT_LOGS_LIST } },
								],
								pageTitle: ROUTER_PAGE_TITLE.AUDIT_LOG_DETAIL,
							},
						},
					],
				},
			],
		},
		{
			path: '/403',
			name: ROUTER_NAME.PERMISSION_DENIED,
			component: PagePermissionDenied,
		},
		{
			path: '/404',
			name: ROUTER_NAME.NOT_FOUND,
			component: PageNotFound,
		},
		{
			path: '*',
			redirect: '/404',
		},
	],
});

// Display meta title by using pageTitle from router configuration.
router.beforeEach((to, from, next) => {
	document.title = to.meta && to.meta.pageTitle ? `${to.meta.pageTitle} | ${ROUTER_PAGE_TITLE.DEFAULT}` : ROUTER_PAGE_TITLE.DEFAULT;
	next();
});

export default router;
